import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { isArray, isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';

import { SiteSelectDropdownComponent } from '@features/site-select-dropdown/site-select-dropdown.component';

import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MultiSelectDropdownComponent } from '@shared/components/multi-select-dropdown/multi-select-dropdown.component';
import { FormControlConstants } from '@shared/constants/form-control.constants';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import {
  InvestigatorTransactionsFilterForm,
  TransactionMode,
} from '@shared/services/investigator-transactions/investigator-transaction-form.model';
import { InvestigatorTransactionsService } from '@shared/services/investigator-transactions/investigator-transactions.service';

import { sourceFilterOptions } from './investigator-transactions-filters.constants';
import { InvestigatorFilterConfig } from './investigator-transactions-filters.model';
import { InvestigatorTransactionsAddFilterComponent } from '../investigator-transactions-add-filter/investigator-transactions-add-filter.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    NoopValueAccessorDirective,
    SiteSelectDropdownComponent,
    MultiSelectDropdownComponent,
    InvestigatorTransactionsAddFilterComponent,
    ButtonComponent,
    AsyncPipe,
  ],
  selector: 'aux-investigator-transactions-filters',
  templateUrl: './investigator-transactions-filters.component.html',
})
export class InvestigatorTransactionsFiltersComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private investigatorTransactionsService = inject(InvestigatorTransactionsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  @Input() editMode$!: BehaviorSubject<TransactionMode>;
  @Input() filterLoading$!: BehaviorSubject<boolean>;
  @Input() gridFiltersFormGroup!: FormGroup;
  @Input() gridLoading$!: Observable<boolean>;
  mapFilterConfigByName = new Map<string, InvestigatorFilterConfig>([
    [
      'Category',
      {
        bindLabel: 'name',
        bindValue: 'name',
        formControlName: 'patient_protocol_sub_types',
        items: this.investigatorTransactionsService.categoryOptions$,
        label: 'Category',
      },
    ],
    [
      'Country',
      {
        bindLabel: 'label',
        bindValue: 'value',
        formControlName: 'countries',
        items: this.investigatorTransactionsService.countryOptions$,
        label: 'Country',
      },
    ],
    [
      'Description',
      {
        bindLabel: 'description',
        bindValue: 'description',
        formControlName: 'patient_protocol_names',
        items: this.investigatorTransactionsService.descriptionOptions$,
        label: 'Description',
      },
    ],
    [
      'Patient Group',
      {
        bindLabel: 'name',
        bindValue: 'id',
        formControlName: 'patient_group_ids',
        items: this.investigatorTransactionsService.patientGroupOptions$,
        label: 'Patient Group',
      },
    ],
    [
      'Protocol Version',
      {
        bindLabel: 'name',
        bindValue: 'id',
        formControlName: 'patient_protocol_version_ids',
        items: this.investigatorTransactionsService.protocolVersionOptions$,
        label: 'Protocol Version',
      },
    ],
    [
      'Source',
      {
        bindLabel: 'name',
        bindValue: 'id',
        formControlName: 'sources',
        items: of(sourceFilterOptions),
        label: 'Source',
      },
    ],
  ]);
  maxDate = FormControlConstants.MAX_VALUE.DATE;
  maxDateForStart = FormControlConstants.MAX_VALUE.DATE;
  minDateForEnd = '';
  optionalFilterList$ = new BehaviorSubject<InvestigatorFilterConfig[]>([]);
  patientOptions$ = this.investigatorTransactionsService.patientOptions$;
  @Input() showSourceColumn$!: Observable<boolean>;

  ngOnInit(): void {
    combineLatest([this.route.queryParams, this.patientOptions$, this.gridLoading$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, options, gridLoading]) => {
        if (gridLoading) {
          return;
        }

        if (params.external_patient_id && options.length) {
          const patient = options.find((o) => o.external_patient_id === params.external_patient_id);

          if (patient) {
            this.gridFiltersFormGroup.get('patient_ids')?.setValue([patient.id]);
          }

          void this.router.navigate([], { queryParams: {}, replaceUrl: true });
        }
      });

    this.gridFiltersFormGroup.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(500),
        distinctUntilChanged(isEqual),
        tap((filters) => {
          if (filters.site_ids) {
            const new_patient_ids = this.investigatorTransactionsService.dynamicPatientIdFilter(
              filters.site_ids,
              filters.patient_ids
            );

            this.gridFiltersFormGroup.get('patient_ids')?.setValue(new_patient_ids);
          }

          this.minDateForEnd = filters.start_activity_date ? filters.start_activity_date : '';
          this.maxDateForStart = filters.end_activity_date
            ? filters.end_activity_date
            : FormControlConstants.MAX_VALUE.DATE;
        })
      )
      .subscribe();

    this.investigatorTransactionsService.inMonthAdjustmentsFilter$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pendingFilter) => {
        if (!pendingFilter) {
          return;
        }

        this.addInMonthAdjustmentsFilters();
      });
  }

  addInMonthAdjustmentsFilters(): void {
    const [startDate, endDate, siteIds] =
      this.investigatorTransactionsService.getInMonthAdjustmentsFilters();

    if (!endDate) {
      return;
    }

    const updatedFilters: InvestigatorTransactionsFilterForm = {
      ...this.gridFiltersFormGroup.value,
      end_activity_date: endDate || null,
      site_ids: siteIds || null,
      start_activity_date: startDate || null,
    };

    this.gridFiltersFormGroup.setValue(updatedFilters);

    this.investigatorTransactionsService.resetInMonthAdjustmentsFilters();
  }

  onRemoveOptionalFilter = (filterName: string, formControlName: string) => () => {
    this.optionalFilterList$.next(
      this.optionalFilterList$.getValue().filter(({ label }) => label !== filterName)
    );

    const control = this.gridFiltersFormGroup.controls[formControlName];

    const shouldReset =
      control && (isArray(control.value) ? !!control.value?.length : control.value);

    if (shouldReset) {
      control.setValue(null);
    }
  };

  resetFilers() {
    if (!this.editMode$.getValue()) {
      this.gridFiltersFormGroup.reset();
    }
  }

  selectOptionalFilterChange(filterName: string) {
    const config = this.mapFilterConfigByName.get(filterName);

    if (config) {
      this.optionalFilterList$.getValue().push(config);
    }
  }
}
